import { useTranslation } from "react-i18next"
import { MagicWand } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"

export type AiFeatureItem = {
  type: string
  name: string
  description: string
  action: () => void
  shouldShow: boolean
  disabled?: boolean
  disabledReason?: string
}

const AIFeatureButton = ({ name, description, action, disabled, disabledReason }: AiFeatureItem) => {
  return (
    <TooltipCustom>
      <TooltipTrigger asChild>
        <DropdownMenuItem
          className="focus:bg-surface-hovered flex-col items-start rounded-md px-2 py-1"
          onClick={action}
          disabled={disabled}>
          <div className="text-sm">{name}</div>
          <div className="text-on-surface-subdued text-xs">{description}</div>
        </DropdownMenuItem>
      </TooltipTrigger>
      {disabled && disabledReason && (
        <TooltipPortal>
          <TooltipContent side="right" align="center">
            <span className="p-1">{disabledReason}</span>
          </TooltipContent>
        </TooltipPortal>
      )}
    </TooltipCustom>
  )
}

type AiInputButtonsProps = {
  items: AiFeatureItem[]
  isRunning: boolean
  /** Override the trigger test id when more than one instance can render on a page. */
  testId?: string
}

export const AiInputButtons = ({ items, isRunning, testId = "get-ai-input-button" }: AiInputButtonsProps) => {
  const { t } = useTranslation()

  const visibleItems = items.filter((item) => item.shouldShow)
  const hasFeature = visibleItems.length > 0

  return (
    <DropdownMenu modal={false}>
      <TooltipCustom>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              data-testid={testId}
              className="grid-cols-1fr-auto border-on-plum-surface bg-plum-surface-subdued text-on-plum-surface hover:bg-plum-surface grid max-h-8 w-fit cursor-pointer gap-2 rounded-md border p-1.5 disabled:cursor-not-allowed disabled:opacity-75"
              disabled={isRunning || !hasFeature}>
              <MagicWand size={20} />
              <span className="text-sm font-medium">{t("chat.input.getAiInput")}</span>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        {!hasFeature && (
          <TooltipPortal>
            <TooltipContent side="bottom" align="start">
              <span className="p-1">{t("chat.input.noAiFeaturesAvailable")}</span>
            </TooltipContent>
          </TooltipPortal>
        )}
      </TooltipCustom>
      {hasFeature && !isRunning && (
        <DropdownMenuContent
          align="start"
          side="top"
          className="rounded-lg p-2"
          onMouseDown={(e) => e.stopPropagation()}>
          {visibleItems.map((item) => (
            <AIFeatureButton key={item.type} {...item} />
          ))}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  )
}
